import { Component, DestroyRef, inject, OnInit } from '@angular/core';

import {
  AboutPanelComponent,
  AboutPanelInputs,
  AuthState,
  ContactFormService,
  LegalDocumentsService,
  NavigationItem,
  SetEnvironment,
  UserState,
  PageLayoutV3Component,
  HeaderComponent,
  SidebarV3Component,
  SidePanelV3,
  SidepanelTypesV3,
} from '@gea/digital-ui-lib';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, filter, map, Observable, of, switchMap, take } from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivationEnd, EventType, Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngxs/store';
import { MiniCartComponentService } from '@spartacus/cart/base/components/mini-cart';
import {
  AuthRedirectService,
  AuthService,
  BaseSiteService,
  CmsLinkComponent,
  CmsService,
  ContentSlotData,
  LanguageService,
  TranslationService,
} from '@spartacus/core';
import { PageSlotComponent } from '@spartacus/storefront';
import {
  ENVIRONMENT_CONFIG,
  EnvironmentConfiguration,
} from '../environments/models/environment.model';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    PageLayoutV3Component,
    HeaderComponent,
    RouterOutlet,
    PageSlotComponent,
    SidebarV3Component,
    AsyncPipe,
  ],
})
export class AppComponent implements OnInit {
  private translate = inject(TranslateService);
  private store = inject(Store);
  private router = inject(Router);
  private destroyed = inject(DestroyRef);
  private miniCartComponentService = inject(MiniCartComponentService);
  private cmsService = inject(CmsService);
  private languageService = inject(LanguageService);
  private baseSiteService = inject(BaseSiteService);
  private userAccountService = inject(AuthService);
  private translationService = inject(TranslationService);
  private contactFormService = inject(ContactFormService);
  protected authRedirectService = inject(AuthRedirectService);
  environment = inject<EnvironmentConfiguration>(ENVIRONMENT_CONFIG);

  private legalDocumentsService = inject(LegalDocumentsService);
  tokenReady = false;

  userReady = false;

  noAuth = false;

  userCountry = 'US';

  currentLanguage = '';

  loggedOutPageReady = false;

  constructor() {
    this.store.select(UserState.token).subscribe(() => {
      this.tokenReady = true;
    });
    this.store
      .select(UserState.loggedOutPageReady)
      .pipe(takeUntilDestroyed(this.destroyed))
      .subscribe((ready) => {
        this.loggedOutPageReady = !!ready;
      });
    this.store
      .select(UserState.authState)
      .pipe(takeUntilDestroyed(this.destroyed))
      .subscribe((authState) => {
        if (authState === AuthState.LogoutRequested) {
          this.tokenReady = false;
          this.loggedOutPageReady = false;
        }
      });
    this.store
      .select(UserState.userState)
      .pipe(takeUntilDestroyed(this.destroyed))
      .subscribe((userState) =>
        this.languageService.setActive(userState.user.language?.split('-')[0] ?? 'en'),
      );
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyed),
        filter((event) => event instanceof ActivationEnd),
      )
      .subscribe((event) => {
        this.noAuth = this.NO_AUTH_ROUTES.includes(event.snapshot?.routeConfig?.path || '');
        // if (event instanceof ActivationEnd) {
        //   this.headerTitle = this.getHeaderTitle();
        // }
      });
  }

  headerTitle$ = this.baseSiteService.getActive().pipe(
    switchMap((baseSiteId) => this.baseSiteService.get(baseSiteId)),
    map((baseSite) => baseSite?.name),
  );

  siteLinkSlotData$ = this.cmsService.getContentSlot('SiteLinks');

  isLoading$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  makeArray(data: ContentSlotData) {
    return data.components?.map((component) =>
      this.cmsService.getComponentData<CmsLinkComponent>(component.uid ?? ''),
    ) as Observable<CmsLinkComponent>[];
  }

  navEntries$ = this.siteLinkSlotData$.pipe(
    map((data) => this.makeArray(data)),
    switchMap((d) => combineLatest(d)),
    map((linkComponents) =>
      linkComponents.map((link) => {
        const iconPath = link.styleAttributes?.match(/portal-icon:([^;]+)/);

        return {
          icon: iconPath ? iconPath[1] : 'bookmark',
          label: link.linkName,
          route: link.url,
        };
      }),
    ),
  );

  adminNavItems: NavigationItem[] = [];

  botItems: SidePanelV3[] = [
    {
      key: 'contact',
      icon: 'chat',
      label: 'UI-LIB.NAV-BAR.CONTACT',
      type: SidepanelTypesV3.CONTACT,
      supportAppBaseUrl: this.environment.support.redirectURL,
    },
    {
      key: 'about',
      icon: 'c-info',
      label: 'UI-LIB.NAV-BAR.ABOUT',
      type: SidepanelTypesV3.FOOTER,
      ref: AboutPanelComponent,
      inputs: {
        copyright: 'GEA Group Services GmbH 2023',
        footers: [
          {
            key: 'imprint-header',
            header: 'UI-LIB.FOOTER.IMPRINT',
            content: [
              {
                key: 'imprint',
                icon: 'pdf',
                label: 'UI-LIB.FOOTER.IMPRINT',
                action: () => this.legalDocumentsService.openImprint(),
              },
            ],
          },
          {
            key: 'privacy-header',
            header: 'UI-LIB.FOOTER.DATAPRIVACY',
            content: [
              {
                key: 'privacy',
                icon: 'pdf',
                label: 'UI-LIB.FOOTER.DATAPRIVACY',
                action: () => this.legalDocumentsService.openDataPrivacy(),
              },
            ],
          },
          {
            key: 'terms-header',
            header: 'UI-LIB.FOOTER.TERMS-AND-CONDITIONS',
            content: [
              {
                key: 'terms',
                icon: 'pdf',
                label: 'UI-LIB.FOOTER.TERMS-AND-CONDITIONS',
                action: () => this.legalDocumentsService.openTermsAndConditions(),
              },
            ],
          },
          {
            key: 'cookie-settings',
            header: 'UI-LIB.FOOTER.COOKIE-SETTINGS',
            content: [
              {
                key: 'cookies',
                label: 'UI-LIB.FOOTER.COOKIES',
                action: () => this.legalDocumentsService.openCookieSettings(),
              },
            ],
          },
        ],
      } as AboutPanelInputs,
    },
  ] as SidePanelV3[];

  // <CHANGE ME> - Add the routes that do not require authentication
  readonly NO_AUTH_ROUTES: string[] = ['about-gea'];

  cartItems$ = this.userAccountService.isUserLoggedIn().pipe(
    switchMap((isLoggedIn) => {
      if (isLoggedIn) {
        return this.miniCartComponentService.getQuantity();
      }
      return of(undefined);
    }),
  );

  ngOnInit(): void {
    // This is the official syntax of ngxs
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.store
      .select(UserState.user)
      .pipe(
        takeUntilDestroyed(this.destroyed),
        filter((user) => !!user.email),
      )
      .subscribe((user) => {
        if (user?.country) {
          this.userCountry = user.country;
        }
        const selectedLanguage = localStorage.getItem('language');
        if (user?.language) {
          this.translate.use(user.language);
        } else if (selectedLanguage) {
          this.translate.use(selectedLanguage);
        } else {
          this.translate.use(this.translate.getFallbackLang() ?? 'en');
        }
        this.languageService.setActive(user.language?.split('-')[0] ?? 'en');
        this.userReady = true;
      });

    if (this.environment.name) {
      this.store.dispatch(new SetEnvironment(this.environment.name));
    }
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyed))
      .subscribe((event: LangChangeEvent) => {
        this.currentLanguage = event.lang;
      });
    this.translate.setDefaultLang('de-DE');

    this.translationService
      .translate('common.commerceErrorCategory')
      .pipe(take(1))
      .subscribe(
        // eslint-disable-next-line no-return-assign
        (commerceCategory) =>
          (this.contactFormService.setCustomCategories = [
            { nameKey: commerceCategory, value: 'salesProQuestion', hasCustomAction: false },
          ]),
      );
    this.initNavLoading();
  }

  initNavLoading() {
    this.router.events.pipe(takeUntilDestroyed(this.destroyed)).subscribe((event) => {
      if (event.type === EventType.NavigationStart) {
        this.isLoading$.next(true);
      }
      if (event.type === EventType.NavigationEnd || event.type === EventType.NavigationError) {
        this.isLoading$.next(false);
      }
    });
  }
}
